/* =============================================================================
   Boardhouse — colour filter grouping (front-end only)

   The Spalva option carries ~1285 distinct supplier values (C56:black/turquoise,
   732 blue-nights, Jet-Black, ...). This groups them into a handful of canonical
   colour swatches at render time, and collapses the matching "Applied filters"
   chips into one removable chip per colour.

   Nothing is written to Shopify: product data, variants and the supplier feed
   are untouched, and new values arriving from a re-sync classify automatically
   by keyword.

   Labels come from the theme locale files via snippets/bh-colour-groups-config
   (window.BHColourGroups), so they translate with the rest of the storefront.

   Degrades safely: without JS the original checkbox list renders as it does now.
   ============================================================================= */

(() => {
  'use strict';

  const DEFAULTS = {
    optionNames: ['Spalva', 'Color', 'Colour'],
    showCounts: false,
    keepFullList: true,
    fullListLabel: 'All shades',
    // true = picking a colour clears the other colours first (single-select)
    exclusive: false,
    squareSwatches: false,
    order: ['Black', 'Grey', 'White', 'Brown', 'Red', 'Orange', 'Yellow',
            'Green', 'Blue', 'Purple', 'Pink', 'Multicolour', 'Assorted'],
    labels: {
      Black: 'Black', Grey: 'Grey', White: 'White', Brown: 'Brown', Red: 'Red',
      Orange: 'Orange', Yellow: 'Yellow', Green: 'Green', Blue: 'Blue',
      Purple: 'Purple', Pink: 'Pink', Multicolour: 'Multicolour', Assorted: 'Assorted'
    },
    swatches: {
      Black: '#000000', Grey: '#808080', White: '#FFFFFF', Brown: '#7B4B2A',
      Red: '#F61F1F', Orange: '#F07B1D', Yellow: '#FFE500', Green: '#2E8B3D',
      Blue: '#005BD3', Purple: '#7B3FB5', Pink: '#F06EA9',
      Multicolour: 'conic-gradient(#f61f1f,#ffe500,#2e8b3d,#005bd3,#7b3fb5,#f61f1f)',
      Assorted: 'repeating-linear-gradient(45deg,#d9d9d9 0 5px,#f4f4f4 5px 10px)'
    }
  };

  // Locale-driven overrides from the Liquid config snippet.
  const external = window.BHColourGroups || {};
  const CONFIG = Object.assign({}, DEFAULTS, external, {
    labels: Object.assign({}, DEFAULTS.labels, external.labels || {}),
    swatches: Object.assign({}, DEFAULTS.swatches, external.swatches || {})
  });
  if (typeof CONFIG.optionNames === 'string') {
    CONFIG.optionNames = CONFIG.optionNames.split(',').map((s) => s.trim()).filter(Boolean);
  }

  // Keyword → canonical colour. A value may match several (black/turquoise).
  const VOCAB = {
    black: 'Black', jet: 'Black', anthracite: 'Black', onyx: 'Black', carbon: 'Black', raven: 'Black',

    white: 'White', wte: 'White', offwhite: 'White', ivory: 'White', cream: 'White',
    chalk: 'White', snow: 'White', vanilla: 'White', bone: 'White', shell: 'White',

    grey: 'Grey', gray: 'Grey', silver: 'Grey', charcoal: 'Grey', graphite: 'Grey',
    steel: 'Grey', shark: 'Grey', concrete: 'Grey', pavement: 'Grey', stone: 'Grey',
    slate: 'Grey', moonlight: 'Grey', heron: 'Grey', fossil: 'Grey', stormy: 'Grey',
    gloomy: 'Grey', iron: 'Grey', moon: 'Grey', ash: 'Grey',

    blue: 'Blue', bl: 'Blue', navy: 'Blue', indigo: 'Blue', teal: 'Blue', petrol: 'Blue',
    cobalt: 'Blue', azure: 'Blue', azur: 'Blue', denim: 'Blue', sky: 'Blue',
    atlantic: 'Blue', cascade: 'Blue', ocean: 'Blue', marine: 'Blue', sea: 'Blue',
    aqua: 'Blue', turquoise: 'Blue', lagoon: 'Blue', cosmic: 'Blue', midnight: 'Blue',
    bluebird: 'Blue', reef: 'Blue', water: 'Blue', aurora: 'Blue',

    green: 'Green', olive: 'Green', khaki: 'Green', mint: 'Green', lime: 'Green',
    limelight: 'Green', sage: 'Green', forest: 'Green', forrest: 'Green', emerald: 'Green',
    moss: 'Green', seek: 'Green', pistachio: 'Green', pistaccio: 'Green', pistacio: 'Green',
    algae: 'Green', seaweed: 'Green', aloe: 'Green', cabbage: 'Green', pine: 'Green',
    grove: 'Green', leaves: 'Green', citronelle: 'Green', bud: 'Green', army: 'Green',
    bamboo: 'Green',

    red: 'Red', scarlet: 'Red', crimson: 'Red', burgundy: 'Red', maroon: 'Red',
    wine: 'Red', cherry: 'Red', ruby: 'Red', rust: 'Red', rusty: 'Red',
    cranberry: 'Red', cabernet: 'Red', firecracker: 'Red', spicy: 'Red',

    pink: 'Pink', rose: 'Pink', magenta: 'Pink', fuchsia: 'Pink', coral: 'Pink',
    blush: 'Pink', salmon: 'Pink', raspberry: 'Pink', grapefruit: 'Pink',

    purple: 'Purple', violet: 'Purple', viola: 'Purple', lilac: 'Purple', lila: 'Purple',
    lavender: 'Purple', plum: 'Purple', tanzanite: 'Purple', grape: 'Purple',
    amethyst: 'Purple', berry: 'Purple', blackberry: 'Purple', aubergine: 'Purple',

    yellow: 'Yellow', gold: 'Yellow', golden: 'Yellow', mustard: 'Yellow', lemon: 'Yellow',
    sun: 'Yellow', sunlight: 'Yellow', amber: 'Yellow', honey: 'Yellow', saffron: 'Yellow',
    harvest: 'Yellow',

    orange: 'Orange', tangerine: 'Orange', apricot: 'Orange', peach: 'Orange',
    tiger: 'Orange', mango: 'Orange', melon: 'Orange', melone: 'Orange',

    brown: 'Brown', tan: 'Brown', beige: 'Brown', sand: 'Brown', sands: 'Brown',
    mud: 'Brown', seal: 'Brown', coffee: 'Brown', chocolate: 'Brown', camel: 'Brown',
    caramel: 'Brown', bronze: 'Brown', walnut: 'Brown', undyed: 'Brown', driftwood: 'Brown',
    wood: 'Brown', pinecone: 'Brown', clay: 'Brown', desert: 'Brown', dune: 'Brown', cob: 'Brown',

    multicolor: 'Multicolour', multicolour: 'Multicolour', multi: 'Multicolour',
    rainbow: 'Multicolour', camo: 'Multicolour', print: 'Multicolour', pattern: 'Multicolour',
    stripes: 'Multicolour', floral: 'Multicolour', glitch: 'Multicolour', gradient: 'Multicolour',
    aop: 'Multicolour', collage: 'Multicolour', palms: 'Multicolour', jungle: 'Multicolour',
    capsule: 'Multicolour', tropical: 'Multicolour', funky: 'Multicolour', crazy: 'Multicolour'
  };

  const ASSORTED = new Set(['c99:random', 'unicolor', 'uni', 'cc99', 'cc00:e4', 'c98:e4', 'c:917']);

  const NOISE = new RegExp(
    '^(s|m|l|xl|xs)[/\\-](s|m|l|xl|xs)$|^one\\s?size$|^\\d+(,\\d+)?\\s*(cm|mm|g|kg)\\b|^\\d+x\\d+|' +
    '^(harness|wetsuits?|helmets?|protection|sustainability|core|element|select|spectre|amaze|amp|atv|' +
    'brush|brushed|gloss|ion\\s+logo|k-sleeve|max_flex|dt_edition|burlando|art of protection|' +
    '3d fit technology|no straight lines|day dreamer|cat grass|harness ergonomics)', 'i');

  const QUALIFIERS = /\b(fuerteventura|tarifa|edition|pet|cold|dusty|dark|deep|light|somber|haze|vivid|neon|acid|spring|under|peak)\b/g;

  const norm = (s) => s.replace(/\s+/g, ' ').trim();
  const key = (s) => norm(s).toLowerCase();

  function classify(rawLabel) {
    const low = key(rawLabel);
    if (ASSORTED.has(low)) return ['Assorted'];
    if (NOISE.test(low)) return [];

    const cleaned = low
      .replace(/^c\d+\s*:/, ' ')
      .replace(/^\d{2,4}x?\s*/, ' ')
      .replace(/\/\d+$/, ' ')
      .replace(QUALIFIERS, ' ');

    const hits = [];
    for (const token of cleaned.split(/[^a-z]+/)) {
      const hit = token && VOCAB[token];
      if (hit && !hits.includes(hit)) hits.push(hit);
    }
    return hits;
  }

  // label (lowercased) -> [canonical names], rebuilt each render
  let labelIndex = new Map();
  // canonical name -> [{input, count, disabled}]
  let memberIndex = new Map();

  function headingOf(group) {
    const el = group.querySelector('.filter-group__heading__text');
    return el ? norm(el.textContent) : '';
  }

  function isColourGroup(group) {
    const heading = headingOf(group).toLowerCase();
    return CONFIG.optionNames.some((n) => n.toLowerCase() === heading);
  }

  function fireChange(fromEl) {
    const form = document.getElementById('CollectionFilterForm');
    (form || fromEl).dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* A colour counts as "picked" only when every one of its selectable shades
     is ticked. Shared shades (403 tiger-coral is Orange and Pink) get ticked
     when a neighbouring colour is picked, which must not make that neighbour
     look picked too. */
  function selectableOf(name) {
    return (memberIndex.get(name) || []).filter((m) => !m.disabled);
  }

  function pickedColours() {
    const picked = new Set();
    memberIndex.forEach((_, name) => {
      const sel = selectableOf(name);
      if (sel.length && sel.every((m) => m.input.checked)) picked.add(name);
    });
    return picked;
  }

  // Untick a colour's shades, keeping any shade another picked colour still needs.
  function releaseColour(name) {
    const keep = new Set();
    pickedColours().forEach((other) => {
      if (other === name) return;
      selectableOf(other).forEach((m) => keep.add(m.input));
    });
    const sel = selectableOf(name);
    sel.forEach((m) => { if (!keep.has(m.input)) m.input.checked = false; });
    return sel[0] ? sel[0].input : null;
  }

  function paintSwatch(el, name) {
    const paint = CONFIG.swatches[name] || '#ccc';
    const isGradient = /gradient/.test(paint);
    el.style.setProperty('--bh-swatch', isGradient ? 'none' : paint);
    el.style.setProperty('--bh-swatch-image', isGradient ? paint : 'none');
  }

  /* ---------- the swatch row inside the colour filter group ---------- */

  function buildSwatches(group) {
    if (group.dataset.bhGrouped === 'true') return;

    const container = group.querySelector('.toggle-target-container');
    if (!container) return;

    const items = [...container.querySelectorAll('.filter-group__item')];
    if (items.length < 2) return;

    const groups = new Map();
    const labels = new Map();
    let classified = 0;

    items.forEach((item) => {
      const input = item.querySelector('.filter-group__checkbox');
      const textEl = item.querySelector('.filter-group__item__text');
      if (!input || !textEl) return;

      const label = norm(textEl.textContent);
      const countEl = item.querySelector('.filter-group__item__count');
      const count = countEl ? parseInt(countEl.textContent.replace(/\D/g, ''), 10) || 0 : 0;

      const hits = classify(label);
      if (hits.length) {
        classified += 1;
        labels.set(key(label), hits);
      }

      hits.forEach((name) => {
        if (!groups.has(name)) groups.set(name, []);
        groups.get(name).push({ input, count, disabled: input.disabled });
      });
    });

    if (!groups.size || classified < items.length * 0.5) return;

    labelIndex = labels;
    memberIndex = groups;
    const picked = pickedColours();

    const row = document.createElement('div');
    row.className = 'bh-cgroup' + (CONFIG.squareSwatches ? ' bh-cgroup--square' : '');

    CONFIG.order.forEach((name) => {
      const members = groups.get(name);
      if (!members || !members.length) return;

      const selectable = members.filter((m) => !m.disabled);
      if (!selectable.length) return;

      const total = members.reduce((sum, m) => sum + m.count, 0);
      const active = picked.has(name);
      const labelText = CONFIG.labels[name] || name;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bh-cgroup__item';
      if (active) btn.classList.add('is-active');
      if (name === 'White') btn.classList.add('is-light');
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      btn.setAttribute('aria-label', CONFIG.showCounts ? labelText + ' (' + total + ')' : labelText);
      paintSwatch(btn, name);

      btn.innerHTML =
        '<span class="bh-cgroup__dot" aria-hidden="true"></span>' +
        '<span class="bh-cgroup__label">' + labelText +
        (CONFIG.showCounts ? ' <span class="bh-cgroup__count">' + total + '</span>' : '') +
        '</span>';

      btn.addEventListener('click', () => {
        if (pickedColours().has(name)) {
          fireChange(releaseColour(name) || selectable[0].input);
          return;
        }
        if (CONFIG.exclusive) {
          memberIndex.forEach((list) => list.forEach((m) => { m.input.checked = false; }));
        }
        selectable.forEach((m) => { m.input.checked = true; });
        fireChange(selectable[0].input);
      });

      row.appendChild(btn);
    });

    if (!row.children.length) return;

    container.classList.add('bh-cgroup__source');
    group.classList.add('bh-cgroup-host');
    container.parentNode.insertBefore(row, container);

    if (CONFIG.keepFullList) {
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'bh-cgroup__more';
      toggle.textContent = CONFIG.fullListLabel;
      toggle.setAttribute('aria-expanded', 'false');
      toggle.addEventListener('click', () => {
        const open = group.classList.toggle('bh-cgroup--expanded');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      container.parentNode.insertBefore(toggle, container);
    }

    group.dataset.bhGrouped = 'true';
  }

  /* ---------- collapse the Applied filters chips ---------- */

  function buildApplied() {
    const block = document.querySelector('.filter-group--applied');
    if (!block || block.dataset.bhGrouped === 'true') return;
    if (!labelIndex.size) return;

    const items = block.querySelector('.filter-group__items');
    if (!items) return;

    const chips = [...items.querySelectorAll('.filter-group__applied-item')];
    if (!chips.length) return;

    // One chip per picked colour. A shade's own chip is hidden only when a
    // picked colour covers it; shades ticked individually from the full list
    // keep their chip. Non-colour chips (size, brand, price) are untouched.
    const picked = pickedColours();
    if (!picked.size) return;

    chips.forEach((chip) => {
      const textEl = chip.querySelector('.filter-group__applied-item__text');
      if (!textEl) return;
      const hits = labelIndex.get(key(textEl.textContent));
      if (hits && hits.some((h) => picked.has(h))) {
        chip.classList.add('bh-cgroup__applied-hidden');
      }
    });

    const frag = document.createDocumentFragment();

    CONFIG.order.forEach((name) => {
      if (!picked.has(name)) return;

      const labelText = CONFIG.labels[name] || name;

      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'filter-group__applied-item bh-cgroup__applied';
      if (name === 'White') chip.classList.add('is-light');
      paintSwatch(chip, name);
      chip.setAttribute('aria-label', labelText);

      chip.innerHTML =
        '<span class="bh-cgroup__dot bh-cgroup__dot--sm" aria-hidden="true"></span>' +
        '<span class="filter-group__applied-item__text">' + labelText + '</span>' +
        '<span class="filter-group__applied-item__icon" aria-hidden="true">' +
          '<svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.3">' +
            '<path d="M5 5l10 10M15 5L5 15"/>' +
          '</svg>' +
        '</span>';

      // One click clears this colour, leaving other picked colours intact.
      chip.addEventListener('click', () => {
        const from = releaseColour(name);
        if (from) fireChange(from);
      });

      frag.appendChild(chip);
    });

    const clearRow = items.querySelector('.filter-group__clear-row');
    items.insertBefore(frag, clearRow || null);
    block.dataset.bhGrouped = 'true';
  }

  /* ---------- lifecycle ---------- */

  function run() {
    document.querySelectorAll('.filter-group').forEach((group) => {
      if (isColourGroup(group)) buildSwatches(group);
    });
    buildApplied();
  }

  let queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; run(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Filters are replaced wholesale on ajax filtering, with no event to hook.
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();